import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useGlobalOrder } from "../../utils/GlobalStateContext";
import api_client from "../../utils/api_client";
import { getReminderSchedule, LEGACY_REMINDERS } from "./posReminderSchedule";
import "./PosReminders.css";

/**
 * POS 定時提醒（倒垃圾、烤雞腿…）。
 * 內容由後台「點餐 → 作業檢核項目 → POS 定時提醒」設定，GET /api/pos/reminders 只回這台機器要顯示的提醒；
 * 後端還沒有這支 API 時用預設提醒（LEGACY_REMINDERS：倒垃圾、17:20 烤雞腿）。
 * 每則提醒在提醒時間那一分鐘跳出並響 5 秒，按完成或過了持續時間就收起；當天提示過就不再補跳。
 */

const CACHE_KEY = "pos_reminders_cache";
const SHOWN_KEY = "pos_reminders_shown"; // { [reminder_id]: "YYYY-MM-DD" }
const DONE_KEY = "pos_reminders_done";
const REFRESH_MS = 5 * 60 * 1000;
// beep.mp3 約 1 秒，每秒播一次共 5 次 ≈ 響 5 秒
const BEEP_TIMES = 5;

const EMOJI = { food: "🍗", bell: "🔔" };

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 儲存不可用時，仍以記憶體狀態運作。
  }
}

function legacyFor(positionId) {
  return LEGACY_REMINDERS.filter((reminder) => reminder.position_ids.includes(Number(positionId)));
}

function TrashMascot() {
  return (
    <svg className="trash-mascot" viewBox="0 0 100 112" aria-hidden="true" focusable="false">
      <ellipse cx="50" cy="104" rx="28" ry="4" fill="#CCE8DE" />
      <g className="trash-mascot-body">
        <path d="M29 70 Q15 68 16 57" fill="none" stroke="#31846C" strokeWidth="5" strokeLinecap="round" />
        <g className="trash-mascot-wave">
          <path d="M73 65 Q88 60 85 45" fill="none" stroke="#31846C" strokeWidth="5" strokeLinecap="round" />
          <path d="M85 46 L80 39 M85 46 L88 37" fill="none" stroke="#31846C" strokeWidth="3" strokeLinecap="round" />
        </g>
        <path d="M38 96 L35 101 M63 96 L66 101" stroke="#31846C" strokeWidth="5" strokeLinecap="round" />
        <path d="M26 39 H75 L70 88 Q69 97 60 97 H41 Q32 97 31 88 Z" fill="#80CFB1" stroke="#31846C" strokeWidth="3" />
        <path d="M35 45 L39 86" stroke="#B9E8D5" strokeWidth="4" strokeLinecap="round" />
        <g className="trash-mascot-lid">
          <rect x="23" y="32" width="55" height="9" rx="4.5" fill="#31846C" />
          <path d="M42 31 V27 Q42 24 46 24 H55 Q59 24 59 27 V31" fill="none" stroke="#31846C" strokeWidth="4" />
        </g>
        <circle cx="42" cy="62" r="3" fill="#215846" />
        <circle cx="60" cy="62" r="3" fill="#215846" />
        <ellipse cx="36" cy="69" rx="5" ry="3" fill="#F5B7AB" />
        <ellipse cx="66" cy="69" rx="5" ry="3" fill="#F5B7AB" />
        <path d="M46 70 Q51 76 56 70" fill="none" stroke="#215846" strokeWidth="2.5" strokeLinecap="round" />
      </g>
      <g className="trash-mascot-sparkles" fill="#E7B44C">
        <path d="M18 23 L20 17 L22 23 L28 25 L22 27 L20 33 L18 27 L12 25 Z" />
        <path d="M80 16 L82 12 L84 16 L88 18 L84 20 L82 24 L80 20 L76 18 Z" />
      </g>
    </svg>
  );
}

function ReminderCard({ reminder, onComplete }) {
  return (
    <Box className="pos-reminder">
      {reminder.icon === "trash" ? (
        <TrashMascot />
      ) : (
        <span className="pos-reminder-emoji" aria-hidden="true">{EMOJI[reminder.icon] || EMOJI.bell}</span>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box role="status" aria-live="polite" aria-atomic="true">
          <Typography sx={{ color: "#56806E", fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>
            {reminder.remind_time} · 定時提醒
          </Typography>
          <Typography sx={{ color: "#215846", fontSize: 22, fontWeight: 900, mt: 0.25 }}>
            {reminder.title}
          </Typography>
          {reminder.message && (
            <Typography sx={{ color: "#56806E", fontSize: 13, mt: 0.25 }}>
              {reminder.message}
            </Typography>
          )}
        </Box>
        <Button
          onClick={onComplete}
          startIcon={<CheckRoundedIcon />}
          sx={{
            mt: 1,
            minHeight: 44,
            px: 2,
            borderRadius: "14px",
            backgroundColor: "#31846C",
            color: "#FFFFFF",
            fontSize: 15,
            fontWeight: 800,
            "&:hover": { backgroundColor: "#266C58" },
          }}
        >
          {reminder.button_text || "完成"}
        </Button>
      </Box>
    </Box>
  );
}

export default function PosReminders() {
  const { systemData, playBeep } = useGlobalOrder();
  const positionId = systemData?.position_id;
  const enabled = Boolean(positionId) && systemData?.client_mode !== "kiosk";
  // null＝還沒拿到清單（沒有快取、API 尚未回應）
  const [reminders, setReminders] = useState(() => readJson(CACHE_KEY, null));
  const [active, setActive] = useState([]); // [{ key, day, reminder }]
  const activeRef = useRef([]);
  const updateActive = (next) => {
    activeRef.current = next;
    setActive(next);
  };
  const [beepToken, setBeepToken] = useState(0);
  const memory = useRef({ shown: {}, done: {} });
  const beepTimer = useRef(null);

  // 從後端取清單：每 5 分鐘重抓，後台改了不用重開 POS。失敗時保留上一次的清單。
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api_client.get("/reminders");
        if (cancelled || data?.error !== 0 || !Array.isArray(data.list)) return;
        writeJson(CACHE_KEY, data.list);
        setReminders(data.list);
      } catch (error) {
        if (cancelled) return;
        // 舊版後端沒有這支 API：用預設提醒（倒垃圾、17:20 烤雞腿）
        if (error?.response?.status === 404) {
          setReminders(legacyFor(positionId));
        } else {
          setReminders((prev) => prev ?? legacyFor(positionId));
        }
      }
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled, positionId]);

  const isMarked = (kind, id, day) => {
    const stored = readJson(kind === "done" ? DONE_KEY : SHOWN_KEY, {});
    return stored[id] === day || memory.current[kind][id] === day;
  };

  const mark = (kind, id, day) => {
    memory.current[kind][id] = day;
    const key = kind === "done" ? DONE_KEY : SHOWN_KEY;
    // 只保留今天的紀錄，避免無限累積
    const stored = Object.fromEntries(Object.entries(readJson(key, {})).filter(([, value]) => value === day));
    writeJson(key, { ...stored, [id]: day });
  };

  const check = useCallback(() => {
    if (!enabled || !reminders) {
      updateActive([]);
      return;
    }
    const now = new Date();
    let started = false;
    const prev = activeRef.current;
    const next = [];
    for (const reminder of reminders) {
      const id = String(reminder.reminder_id);
      const schedule = getReminderSchedule(reminder, now);
      if (!schedule || isMarked("done", id, schedule.day)) continue;
      const showing = prev.find((item) => item.key === id && item.day === schedule.day);
      if (showing) {
        next.push({ ...showing, reminder });
        continue;
      }
      if (schedule.shouldStart && document.visibilityState !== "hidden" && !isMarked("shown", id, schedule.day)) {
        mark("shown", id, schedule.day);
        started = true;
        next.push({ key: id, day: schedule.day, reminder });
      }
    }
    updateActive(next);
    if (started) setBeepToken((token) => token + 1);
    // isMarked／mark／updateActive 只讀寫 localStorage 與 ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reminders]);

  useEffect(() => {
    if (!enabled) {
      updateActive([]);
      return undefined;
    }
    let timer;
    const tick = () => {
      check();
      // 對齊下一個整分鐘，準時在提醒時間檢查，也處理跨日。
      timer = setTimeout(tick, 60000 - (Date.now() % 60000));
    };
    tick();
    window.addEventListener("storage", check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("storage", check);
    };
  }, [enabled, check]);

  // 有新提醒跳出時響 5 秒；用自己的計時器呼叫 playBeep，不動到新訂單提醒共用的 startBeep/stopBeep
  useEffect(() => {
    if (!beepToken || !playBeep) return undefined;
    playBeep();
    let played = 1;
    beepTimer.current = setInterval(() => {
      playBeep();
      played += 1;
      if (played >= BEEP_TIMES) clearInterval(beepTimer.current);
    }, 1000);
    return () => clearInterval(beepTimer.current);
  }, [beepToken, playBeep]);

  const complete = (item) => {
    clearInterval(beepTimer.current);
    mark("done", item.key, item.day);
    updateActive(activeRef.current.filter((other) => other.key !== item.key));
  };

  if (!enabled || active.length === 0) return null;

  return (
    <Box className="pos-reminder-stack" sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}>
      {active.map((item) => (
        <ReminderCard key={item.key} reminder={item.reminder} onComplete={() => complete(item)} />
      ))}
    </Box>
  );
}
